import React, { ChangeEvent, useState } from 'react'

import {
  TextInput,
  Typography,
  Button,
  useDimensions,
  Spinner,
} from '@telavita-core/react-design-kit'

import ModalGeneric from 'containers/ModalManager/Modals/ModalGeneric'

import LineSeparator from 'components/LineSeparator'

import { useValidateToken } from './hooks/useValidateToken'
import { useProfile } from 'hooks'

import { planGroupsEnabledScheduleToken } from 'utils/isScheduleTokenEnabled'

import { ReactComponent as TokenIcon } from 'static/svg/token.svg'

import * as S from './styles'

const ERRORS_MESSAGE = {
  503: 'No momento, seu convênio encontra-se indisponível para verificação do token. Tente novamente.',
  424: 'Código inválido. Verifique novamente o token.',
  400: 'Código inválido. Verifique novamente o token.',
} as const

interface RequestTokenModalProps {
  planGroupName: string
  planGroupCode: string
  onClose: () => void
  scheduleId: number
  onClickJoinVirtualRoom: () => void
}

interface PlanGroupInstructions {
  first: {
    textLineOne: string
    textLineTwo: string
  }
  second: string
}

function getInstructionsByPlanGroupCode(planGroupCode: string, planGroupName: string): PlanGroupInstructions {
  if ([planGroupsEnabledScheduleToken.CUIDAR_OMINT_PLAN_GROUP_CODE, planGroupsEnabledScheduleToken.OMINT_PLAN_GROUP_CODE].includes(planGroupCode)) {
    return {
      first: {
        textLineTwo: 'Credencial.',
        textLineOne: `Entre no app da ${planGroupName} e vá em`,
      },
      second: `O token ${planGroupName} está localizado abaixo das informações de sua carteirinha.`,
    } as PlanGroupInstructions
  }
  if (planGroupCode === planGroupsEnabledScheduleToken.SAMI_PLAN_GROUP_CODE) {
    return {
      first: {
        textLineTwo: 'Carteirinha e Token.',
        textLineOne: `Entre no app da ${planGroupName} e vá em`,
      },
      second: 'Acesse a aba “token” para visualizar os números.',
    } as PlanGroupInstructions
  }
}

export const RequestTokenModal = ({
  planGroupName,
  planGroupCode,
  onClose,
  scheduleId,
  onClickJoinVirtualRoom,
}: RequestTokenModalProps): JSX.Element => {
  const { isMobile } = useDimensions()
  const { isPatient } = useProfile()
  const {
    isSubmitting,
    isRedirecting,
    errorStatusCode,
    onSubmit
  } = useValidateToken()

  const [tokenValue, setTokenValue] = useState('')

  const isDisabledSubmitButton = tokenValue.length < 1 || isSubmitting

  const instructions = getInstructionsByPlanGroupCode(planGroupCode, planGroupName)

  function handleChangeTokenValue(event: ChangeEvent<HTMLInputElement>) {
    setTokenValue(event.target.value)
  }

  async function handleSubmit() {
    const { isValid } = await onSubmit(scheduleId,tokenValue)

    if (!isValid) return

    setTimeout(() => {
      onClickJoinVirtualRoom()
    }, 3000)
  }

  if (!isPatient) return null

  return (
    <ModalGeneric
      modalTitle={
        isRedirecting ?
          <S.SpinnerContainer>
            <Spinner customClassName='RequestTokenModal__Spinner' />
          </S.SpinnerContainer>
          :
          <S.Title>
            Para realizar a sua consulta, é preciso inserir o token do seu aplicativo {planGroupName}.
          </S.Title>
      }
      hasCloseButton={!isRedirecting}
      onCloseModal={onClose}
    >
      <S.Container>
        {isRedirecting
          ? (
            <Typography
              variant='header4'
              center
              weight='bold'
            >
              Token enviado com sucesso. Redirecionando para a consulta.
            </Typography>
          )
          : (
            <>
              <TokenIcon />
              <S.TokenInputWrapper>
                <TextInput
                  name='token'
                  placeholder='Digite o código*'
                  onInputChange={handleChangeTokenValue}
                  type={isMobile ? 'number' : 'text'}
                  hasError={Boolean(errorStatusCode)}
                  messageError={ERRORS_MESSAGE[errorStatusCode]}
                  disabled={isSubmitting}
                />
              </S.TokenInputWrapper>
              <Typography
                italic
                variant='content3'
                customClassName='RequestTokenModal__Label'
              >
                *Preenchimento obrigatório
              </Typography>
              <Button
                disabled={isDisabledSubmitButton}
                variant='contained'
                customClassName='RequestTokenModal__SubmitButton'
                onClick={handleSubmit}
              >
                {isSubmitting ? <Spinner /> : 'Enviar token'}
              </Button>
              <LineSeparator
                lineColor="#e2e2e2"
                lineHeight="1px"
                lineMargin="30px 0"
                lineWidth="100%"
              />
              <S.TokenInfoContainer>
                <Typography
                  weight='bold'
                >
                  Como localizar o token?
                </Typography>
                <ul>
                  <li>
                    <div>
                      <span>1</span>
                    </div>
                    <Typography variant='content2'>
                      {instructions?.first?.textLineOne}
                      <br />
                      <Typography
                        variant='content2'
                        weight='bold'
                        italic={planGroupCode === planGroupsEnabledScheduleToken.SAMI_PLAN_GROUP_CODE}
                      >
                        {instructions?.first?.textLineTwo}
                      </Typography>
                    </Typography>
                  </li>
                  <li>
                    <div>
                      <span>2</span>
                    </div>
                    <Typography variant='content2'>
                      {instructions?.second}
                    </Typography>
                  </li>
                </ul>
              </S.TokenInfoContainer>
            </>
          )
        }
      </S.Container>
    </ModalGeneric>
  )
}